/**
 * Helpers that decide whether a reconstructed assistant text segment is worth
 * rendering as its own visible message bubble.
 *
 * Why this exists: around tool activity the model sometimes emits a garbled or
 * partial markdown structure (e.g. it wrote `[ ...tool... ]` and, once the
 * tool block was extracted into its own group, the surrounding `[` / `]`
 * survived as a standalone text segment). A finalized assistant message then
 * rendered a lone, stray `]` as its own bubble above the tool-result card.
 *
 * These helpers drop a text segment whose ENTIRE trimmed content is only
 * orphan markdown structural punctuation (brackets/parens/braces + whitespace),
 * and strip a leading/trailing orphan punctuation line that is glued to
 * otherwise-real content. Legitimate prose that merely CONTAINS brackets
 * (`[label](url)`, `array[0]`, "see item [3] below") is always preserved,
 * because the guard only fires when nothing but structural punctuation remains.
 */

// Characters we treat as orphan markdown structure: the bracket/paren/brace
// pairs the model leaks around tool activity, plus surrounding whitespace.
// Intentionally narrow — no letters, digits, or sentence punctuation — so a
// segment is only dropped when it carries no readable content at all.
const STRUCTURAL_ONLY = /^[[\](){}\s]+$/;

/**
 * True when the whole (already trimmed) string is nothing but orphan
 * bracket/paren/brace punctuation and whitespace. An empty string is NOT
 * structural-only (it's just empty); callers handle emptiness separately.
 */
export function isStructuralOnly(value: string): boolean {
  return STRUCTURAL_ONLY.test(value);
}

/**
 * Whether a text segment should produce a visible message bubble.
 *
 * Returns false for visually-empty content (empty / whitespace only) and for
 * content whose entire trimmed value is orphan structural punctuation
 * (`]`, `[`, `)`, `(`, `}`, `{`, and combinations/whitespace). Returns true
 * for any segment that contains real readable content, even if that content
 * also contains brackets.
 */
export function isRenderableTextSegment(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed) return false;
  return !isStructuralOnly(trimmed);
}

/** A line is droppable when it is blank or only structural punctuation. */
function isBlankOrStructuralLine(line: string): boolean {
  const trimmed = line.trim();
  return !trimmed || isStructuralOnly(trimmed);
}

/**
 * Remove leading and trailing lines that are blank or only structural
 * punctuation, while leaving the inner real content (and its internal blank
 * lines) untouched. This rescues an otherwise-real segment whose first line is
 * an orphan bracket, e.g. `"]\n\nAquí va mi reporte..."` -> `"Aquí va mi
 * reporte..."`. A segment that is ENTIRELY structural collapses to "".
 */
export function stripStructuralOnlyLines(content: string): string {
  const lines = content.split("\n");
  let start = 0;
  let end = lines.length;
  while (start < end && isBlankOrStructuralLine(lines[start])) start++;
  while (end > start && isBlankOrStructuralLine(lines[end - 1])) end--;
  return lines.slice(start, end).join("\n");
}

/**
 * Normalize a candidate text-segment string into the content that should be
 * rendered, or `null` if it should not produce a bubble at all.
 *
 * It first peels off any orphan leading/trailing structural-only lines, then
 * drops the segment entirely when nothing renderable remains.
 */
export function toRenderableSegmentContent(content: string): string | null {
  const cleaned = stripStructuralOnlyLines(content);
  return isRenderableTextSegment(cleaned) ? cleaned : null;
}
